import React, { useEffect } from "react";
import { tBoth } from "../../../core/i18n";

export type NocTypeKey =
  | "OUTSIDE_JOB"
  | "PASSPORT"
  | "HIGHER_EDUCATION"
  | "FOREIGN_VISIT"
  | "ONLINE_COURSE";

export interface NocOverviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  nocType: NocTypeKey;
}

const NocOverviewModal: React.FC<NocOverviewModalProps> = ({
  isOpen,
  onClose,
  onProceed,
  nocType,
}) => {
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") onClose();
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = originalOverflow;
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const renderModalContent = () => {
    switch (nocType) {
      case "OUTSIDE_JOB":
        return (
          <>
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
              <h5 className="mb-0 text-dark fw-bold">
                NOC for outside job overview
              </h5>
              <button
                type="button"
                className="btn btn-link text-danger text-decoration-none p-0"
                onClick={onClose}
                aria-label="Close"
              >
                <i className="ti ti-x fs-24"></i>
              </button>
            </div>

            <div className="p-4" style={{ lineHeight: "1.6" }}>
              <div className="text-dark">
                <p className="mb-3">
                  <strong>1.</strong> {tBoth("noc_overview_point_1")}
                </p>
                <p className="mb-3">
                  <strong>2.</strong> {tBoth("noc_overview_point_2")}
                </p>
                <p className="mb-3">
                  <strong>3.</strong> {tBoth("noc_overview_point_3")}
                </p>
                <p className="mb-3">
                  <strong>4.</strong> {tBoth("noc_overview_point_4")}
                </p>
                <p className="mb-3">
                  <strong>5.</strong> {tBoth("noc_overview_point_5")}
                </p>
                <p className="mb-3">
                  <strong>6.</strong> {tBoth("noc_overview_point_6")}
                </p>
                <p className="mb-3">
                  <strong>7.</strong> {tBoth("noc_overview_point_7")}
                </p>
                <p className="mb-3">
                  <strong>8.</strong> {tBoth("noc_overview_point_8")}
                </p>
                <p className="mb-3">
                  <strong>9.</strong> {tBoth("noc_overview_point_9")}
                </p>
              </div>
            </div>
          </>
        );

      case "PASSPORT":
        return (
          <>
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
              <h5 className="mb-0 text-dark fw-bold">
                NOC for Passport overview
              </h5>
              <button
                type="button"
                className="btn btn-link text-danger text-decoration-none p-0"
                onClick={onClose}
                aria-label="Close"
              >
                <i className="ti ti-x fs-24"></i>
              </button>
            </div>

            <div className="p-4" style={{ lineHeight: "1.6" }}>
              <div className="text-dark">
                <p className="mb-3">
                  <strong>1.</strong> {tBoth("noc_passport_point_1")}
                </p>
                <p className="mb-3">
                  <strong>2.</strong> {tBoth("noc_passport_point_2")}
                </p>
                <p className="mb-3">
                  <strong>3.</strong> {tBoth("noc_passport_point_3")}
                </p>
                <p className="mb-3">
                  <strong>4.</strong> {tBoth("noc_passport_point_4")}
                </p>
              </div>
            </div>
          </>
        );

      case "HIGHER_EDUCATION":
        return (
          <>
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
              <h5 className="mb-0 text-dark fw-bold">
                NOC for higher studies overview
              </h5>
              <button
                type="button"
                className="btn btn-link text-danger text-decoration-none p-0"
                onClick={onClose}
                aria-label="Close"
              >
                <i className="ti ti-x fs-24"></i>
              </button>
            </div>

            <div className="p-4" style={{ lineHeight: "1.8" }}>
              <h6 className="fw-bold mb-3 text-primary">
                CHECK LIST FOR HIGHER QUALIFICATION
              </h6>
              <div className="text-dark">
                <p className="mb-1">
                  <strong>1.</strong> Higher Qualification can be done only after completing one year after probation.
                </p>
                <p className="mb-3 text-muted">
                  1. परीक्षण के बाद एक वर्ष पूरा करने पर उच्च योग्यता प्राप्त की जा सकती है।
                </p>

                <p className="mb-1">
                  <strong>2.</strong> The employee shall ensure that the course of study is recognized by concerned Govt. body like AICTE, UGC, DEC etc.
                </p>
                <p className="mb-3 text-muted">
                  2. कर्मचारी यह सुनिश्चित करेगा कि अध्ययन का पाठ्यक्रम संबंधित सरकारी निकायों यथा AICTE, UGC, DEC आदि द्वारा मान्यता प्राप्त है।
                </p>

                <p className="mb-1">
                  <strong>3.</strong> Only those employees will be eligible for the incentive if they have secured a minimum of 60% marks or its equivalent grade.
                </p>
                <p className="mb-3 text-muted">
                  3. केवल वही कर्मचारी प्रोत्साहन राशि के पात्र होंगे जिन्होंने न्यूनतम 60% अंक या इसके समकक्ष ग्रेड प्राप्त किया हो।
                </p>

                <p className="mb-1">
                  <strong>4.</strong> Application will be accepted through ESS portal only.
                </p>
                <p className="mb-3 text-muted">
                  4. आवेदन केवल ईएसएस पोर्टल के माध्यम से ही स्वीकार किया जाएगा।
                </p>

                <p className="mb-1">
                  <strong>5.</strong> No employee will be given permission to pursue more than one course at any given part of time.
                </p>
                <p className="mb-3 text-muted">
                  5. किसी भी कर्मचारी को किसी भी समय एक से अधिक कोर्स करने की अनुमति नहीं दी जाएगी।
                </p>

                <p className="mb-1">
                  <strong>6.</strong> Prior approval from the competent authority is required for Higher Education registration.
                </p>
                <p className="mb-3 text-muted">
                  6. उच्च शिक्षा पंजीकरण के लिए सक्षम प्राधिकारी से पूर्व अनुमोदन अपेक्षित है।
                </p>
              </div>
            </div>
          </>
        );

      case "FOREIGN_VISIT":
        return (
          <>
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
              <h5 className="mb-0 text-dark fw-bold">
                NOC ex-india leave overview
              </h5>
              <button
                type="button"
                className="btn btn-link text-danger text-decoration-none p-0"
                onClick={onClose}
                aria-label="Close"
              >
                <i className="ti ti-x fs-24"></i>
              </button>
            </div>

            <div className="p-4" style={{ lineHeight: "1.6" }}>
              <h6 className="fw-bold mb-3 text-center text-primary">
                Check List for applying for NOC for Ex-India Leave / विदेश जाने के लिए अवकाश हेतु एनओसी की चैकलिस्ट
              </h6>

              <h6 className="fw-bold mb-3">
                List of documents to be Uploaded / अपलोड किए जाने वाले आवश्यक दस्तावेजों की सूची:
              </h6>

              <div className="text-dark">
                {/* Section 1 */}
                <div className="mb-3 p-2 bg-light rounded border">
                  <p className="mb-1 fw-bold text-dark">
                    1. If expenditure for Ex-India visit in respect of travel and stay will be borne by Employee
                  </p>
                  <p className="mb-2 text-muted fw-semibold">
                    1. यदि भारत से बाहर जाने पर यात्रा और ठहरने के खर्च में व्यय कर्मचारी द्वारा वहन किया जाएगा
                  </p>
                  <ul className="mb-0 ps-3">
                    <li>Bank Statement / बैंक स्टेटमेंट</li>
                    <li>Leave Approval / छुट्टी का अनुमोदन</li>
                    <li>D & AR Status / डीएंडएआर की स्थिति</li>
                  </ul>
                </div>

                {/* Section 2 */}
                <div className="mb-3 p-2 bg-light rounded border">
                  <p className="mb-1 fw-bold text-dark">
                    2. If expenditure for Ex-India visit in respect of travel and stay will be borne by Spouse
                  </p>
                  <p className="mb-2 text-muted fw-semibold">
                    2. यदि भारत से बाहर जाने पर यात्रा और ठहरने के खर्च में व्यय पति/पत्नी द्वारा वहन किया जाएगा
                  </p>
                  <ul className="mb-0 ps-3">
                    <li>Bank Statement of spouse / पति/पत्नी का बैंक स्टेटमेंट</li>
                    <li>Undertaking by spouse / पति/पत्नी द्वारा अंडरटेकिंग</li>
                    <li>Leave Approval / छुट्टी का अनुमोदन</li>
                    <li>D & AR Status / डीएंडएआर की स्थिति</li>
                  </ul>
                </div>

                {/* Section 3 */}
                <div className="mb-3 p-2 bg-light rounded border">
                  <p className="mb-1 fw-bold text-dark">
                    3. If expenditure for Ex-India visit in respect of travel and stay will be borne by relative / friend / Company
                  </p>
                  <p className="mb-2 text-muted fw-semibold">
                    3. यदि विदेश यात्रा के लिए यात्रा और ठहरने का खर्च रिश्तेदार/मित्र/कंपनी द्वारा वहन किया जाएगा
                  </p>
                  <ul className="mb-0 ps-3">
                    <li>Invitation Letter from relative/friend/Company / रिश्तेदार/मित्र/कंपनी से निमंत्रण</li>
                    <li>Address Proof / निवास प्रमाणपत्र</li>
                    <li>Undertaking / अंडरटेकिंग</li>
                    <li>Leave Approval / छुट्टी का अनुमोदन</li>
                    <li>D & AR Status / डीएंडएआर की स्थिति</li>
                  </ul>
                </div>

                {/* Section 4 */}
                <div className="mb-3 p-2 bg-light rounded border">
                  <p className="mb-1 fw-bold text-dark">
                    4. If expenditure for Ex-India visit in respect of travel will be borne by employee and stay will be borne by relative / friend / Company
                  </p>
                  <p className="mb-2 text-muted fw-semibold">
                    4. यदि यात्रा के खर्च में भारत से बाहर जाने का खर्च कर्मचारी द्वारा वहन किया जाएगा और रहने का खर्च रिश्तेदार/मित्र/कंपनी द्वारा वहन किया जाएगा।
                  </p>
                  <ul className="mb-0 ps-3">
                    <li>Invitation Letter from relative/friend/Company / रिश्तेदार/मित्र/कंपनी से निमंत्रण</li>
                    <li>Address Proof / निवास प्रमाणपत्र</li>
                    <li>Bank Statement of Employee / कर्मचारी का बैंक स्टेटमेंट</li>
                    <li>Undertaking / अंडरटेकिंग</li>
                    <li>Leave Approval / छुट्टी का अनुमोदन</li>
                    <li>D & AR Status / डीएंडएआर की स्थिति</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        );

      case "ONLINE_COURSE":
        return (
          <>
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
              <h5 className="mb-0 text-dark fw-bold">
                CHECKLIST FOR APPROVAL OF NOC FOR ONLINE COURSES
              </h5>
              <button
                type="button"
                className="btn btn-link text-danger text-decoration-none p-0"
                onClick={onClose}

                aria-label="Close"
              >
                <i className="ti ti-x fs-24"></i>
              </button>
            </div>

            <div className="p-4" style={{ lineHeight: "1.6" }}>
              <div className="text-dark">
                <p className="mb-1">
                  <strong>1.</strong> Must have at least two years of job (Regular Basis/ Contract Basis) to do online course.
                </p>
                <p className="mb-3 text-muted">
                  १. ऑनलाइन कोर्स करने के लिए कम से कम दो वर्ष की नौकरी (नियमित/संविदात्मक आधार पर) होनी चाहिए।
                </p>

                <p className="mb-1">
                  <strong>2.</strong> Courses have been identified from approved courses list of DMRA.
                </p>
                <p className="mb-3 text-muted">
                  २. डीएमआरए की अनुमोदित पाठ्यक्रम सूची से पाठ्यक्रमों की पहचान की गई है।
                </p>

                <p className="mb-1">
                  <strong>3.</strong> Generic course can be done by employees of any department.
                </p>
                <p className="mb-3 text-muted">
                  ३. किसी भी विभाग का कर्मचारी जेनेरिक कोर्स कर सकता है।
                </p>

                <p className="mb-1">
                  <strong>4.</strong> (A2) Specialized course can be done by employees of respective discipline only.
                </p>
                <p className="mb-3 text-muted">
                  ४. केवल संबंधित विभागों के कर्मचारी ही विशिष्ट पाठ्यक्रम (A2) कर सकते हैं।
                </p>

                <p className="mb-1">
                  <strong>5.</strong> Total course fee or single course fee in a block of 3 year should not exceed Rs 18000.
                </p>
                <p className="mb-3 text-muted">
                  ५. तीन वर्ष के ब्लॉक में कुल पाठ्यक्रम शुल्क या एकल पाठ्यक्रम शुल्क 18000 रु. से अधिक नहीं होना चाहिए।
                </p>

                <p className="mb-1">
                  <strong>6.</strong> The duration of the course should be minimum 3 weeks and maximum 12 weeks.
                </p>
                <p className="mb-3 text-muted">
                  ६. कोर्स की अवधि न्यूनतम 3 सप्ताह और अधिकतम 12 सप्ताह होनी चाहिए।
                </p>

                <p className="mb-1">
                  <strong>7.</strong> Employees of Rolling Stock & Electrical department can do course from list of either department.
                </p>
                <p className="mb-3 text-muted">
                  ७. रोल स्टॉक और विद्युत विभाग के कर्मचारी दोनों में से किसी भी विभाग की सूची वाले कोर्स कर सकते हैं।
                </p>

                <p className="mb-1">
                  <strong>8.</strong> Each employee's block year, which will begin from the date of first course by the employee.
                </p>
                <p className="mb-3 text-muted">
                  ८. प्रत्येक कर्मचारी का ब्लॉक वर्ष, कर्मचारी द्वारा प्रथम कोर्स की तिथि से शुरू होगा।
                </p>

                <p className="mb-1">
                  <strong>9.</strong> Prior approval from the competent authority is required for online course admission.
                </p>
                <p className="mb-3 text-muted">
                  ९. ऑनलाइन पाठ्यक्रम में प्रवेश के लिए सक्षम प्राधिकारी का पूर्व अनुमोदन आवश्यक है।
                </p>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div
        className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
        style={{
          zIndex: 1050,
          width: "90%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        role="dialog"
        aria-modal="true"
      >
        {renderModalContent()}

        {/* OK / Proceed Button */}
        <div className="d-flex justify-content-end p-4 pt-0">
          <button
            type="button"
            className="btn text-white px-5 py-2 fw-bold"
            style={{
              backgroundColor: "#B85450",
              borderRadius: "25px",
              fontSize: "16px",
            }}
            onClick={onProceed}
          >
            OK
          </button>
        </div>
      </div>
    </>
  );
};

export default NocOverviewModal;
